<script setup lang="ts">
import { computed, watch, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useSelectionStore } from '@/stores/selection'
import { useJudgmentOverview, useOfferDetail, useRelatedFileCounts } from '@/api/queries'

import TopBar from '@/components/TopBar.vue'
import FilterPanel from '@/components/FilterPanel.vue'
import OfferTable from '@/components/OfferTable.vue'
import NgWordPanel from '@/components/NgWordPanel.vue'
import SellerInfoPanel from '@/components/SellerInfoPanel.vue'
import OfferDetailPanel from '@/components/OfferDetailPanel.vue'
import NgHitDetailPane from '@/components/NgHitDetailPane.vue'
import PaneDivider from '@/components/PaneDivider.vue'
import { useLayoutStore } from '@/stores/layout'
import ActionBar from '@/components/ActionBar.vue'

const selection = useSelectionStore()
const route = useRoute()

/**
 * URLで ?asin=B0XXXXXXXX が指定された場合は、その商品を最初から表示する。
 * (商品編集Webから審査Webを開く場合など、対象を直接指定したいケース向け)
 *
 * 明示的にASINを指定しているので、既定の絞り込み(プレミアムのみ・審査状態)は
 * 外しておく。これを残すと、指定したASINが条件に合わず一覧に出てこないことがある。
 */
const initialAsin = (route.query.asin as string | undefined)?.trim() || null
if (initialAsin) {
  // ASINを名指ししている以上、その1件は必ず出したい。
  // 既定の絞り込みが残っていると、条件に合わない商品が「表示されない」ため
  // 全て解除する。特に以下の3つが取りこぼしの原因になっていた:
  //   - judgmentSubStatusCode: 既定が「審査中(審査)」なので、他の状態のASINが出ない
  //   - includeDeleted:        既定で DeleteFlg='0' に絞られ、削除済み・一時停止中が出ない
  //   - includeDisabled:       既定で EnableFlg='1' に絞られ、販売無効化中が出ない
  selection.filters = {
    childAsin: initialAsin,
    includeDeleted: true,
    includeDisabled: true,
  }
  selection.filterDisplay = { childAsin: initialAsin }
  selection.premiumOnly = false
  selection.statusFilter = 'all'
}

const overviewParams = computed(() => selection.effectiveParams)
const { data: overview } = useJudgmentOverview(overviewParams)

// 一覧が返ってきた時点で、指定ASINの行を自動選択する(初回のみ)。
// sellerIdは一覧の結果から取る必要があるため、URLだけでは決められない。
const autoSelected = ref(false)
watch(
  () => overview.value,
  (ov) => {
    if (!initialAsin || autoSelected.value || !ov?.items?.length) return
    const hit = ov.items.find((i) => i.childAsin === initialAsin) ?? ov.items[0]
    if (hit) {
      selection.select(hit.sellerId, hit.childAsin)
      autoSelected.value = true
    }
  },
  { immediate: true },
)

const layout = useLayoutStore()
const isLeftVisible = computed(() => layout.leftPaneVisible.judge)
const asinRef = computed(() => selection.selectedAsin)
const sellerIdRef = computed(() => selection.selectedSellerId)
const { data: detail } = useOfferDetail(asinRef, sellerIdRef)

// 関連ファイル数バッジ。OfferDetailPanelと同じ引数で呼ぶため、
// vue-queryのキャッシュが共有されリクエストは1回で済む。
const { data: fileCounts } = useRelatedFileCounts(
  computed(() => detail.value?.parentAsin ?? null),
  asinRef,
  sellerIdRef,
)
</script>

<template>
  <div id="app-shell" :class="{ 'left-hidden': !isLeftVisible }">
    <!-- NGワードのヒット詳細(本文のマークにホバーするとその真下にフローティング表示) -->
    <NgHitDetailPane />

    <TopBar :total-count="overview?.totalCount ?? 0" :shown-count="overview?.count ?? 0" />

    <div
      class="workspace"
      :class="{ 'workspace--left-hidden': !isLeftVisible }"
      :style="isLeftVisible ? { gridTemplateColumns: `${layout.leftPaneWidth.judge}px 6px 1fr` } : undefined"
    >
      <div v-show="isLeftVisible" class="col col-left">
        <FilterPanel />
        <OfferTable />
        <SellerInfoPanel
          :seller="detail?.seller"
          :file-count="fileCounts?.seller ?? null"
          :annotations="detail?.sellerAnnotations"
        />
        <NgWordPanel :product="detail?.product" />
      </div>

      <PaneDivider v-if="isLeftVisible" screen="judge" />

      <div class="col col-center">
        <OfferDetailPanel />
      </div>
    </div>

    <ActionBar />
  </div>
</template>

<style scoped>
#app-shell {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-width: 1480px;
}
/* 左ペインを畳んだときは、その分だけ必要な最小幅も下げる
   (畳んだのに横スクロールが残ると意味がないため) */
#app-shell.left-hidden {
  min-width: 760px;
}
</style>
